import { Button } from "@screencloud/screencloud-ui-components";
import { FormattedMessage } from "react-intl";
import { formatted } from "src/billinglatest/util/currency";
import darkPixiIcon from "src/pages/Welcome/images/dark-pixi-icon.svg";
import {
  emptyPixiAddress,
  type PixiWizardSelection,
} from "../../hooks/usePixiWizardState";
import { useCheckoutPricing } from "../CheckoutStep/useCheckoutPricing";
import { maskEmail } from "./utils/maskEmail";
import {
  CompleteButtonRow,
  CompleteLayout,
  Divider,
  FreeDeviceLabel,
  Header,
  Heading,
  LicenseValue,
  PixiInlineLogo,
  PixiSavings,
  RenewSubtext,
  RowLabel,
  RowValue,
  SubTitle,
  SuccessIcon,
  SummaryCard,
  SummaryRow,
} from "./styles";

export interface CompleteStepProps {
  state: PixiWizardSelection;
  /** Exits the wizard (closes the modal). */
  onComplete: () => void;
}

/**
 * The post-purchase success screen (GRWBKK-1663, redesigned in GRWBKK-1729).
 * Renders inside the wizard modal in place of Checkout once the purchase
 * succeeds, as a single "order summary" card that matches the Figma success
 * state. Values come from the shared checkout pricing (repoint to the
 * customer-create response when that call lands).
 *
 * The summary shows the license count and the subscription price that is
 * actually charged (`subscriptionTotalCents`, i.e. licences already net of any
 * discount). The free-PIXI deal is a single line — `$0` with the device value
 * as a saving — NOT a calculated device-cost + discount + shipping breakdown
 * (GRWBKK-1729 reverses the GRWBKK-1672 itemisation). The discount deal folds
 * its saving into the license price and hides the PIXI line entirely.
 */
export const CompleteStep = ({ state, onComplete }: CompleteStepProps) => {
  const {
    planContent,
    currency,
    billingCycle,
    licenses,
    isFreeDevice,
    subscriptionTotalCents,
    deviceSavingsCents,
  } = useCheckoutPricing(state);

  const price = (cents: number) => formatted(currency, cents);
  const billingAddress = state.billingAddress ?? emptyPixiAddress();
  const maskedEmail = maskEmail(billingAddress.email);
  const isYearly = billingCycle === "year";

  return (
    <CompleteLayout data-testid="complete-step">
      <Header>
        <SuccessIcon
          name="checked-circle-fill"
          aria-hidden
          data-testid="complete-success-icon"
        />
        <Heading>
          <FormattedMessage
            id="pixi_wizard.complete.title"
            defaultMessage="You're officially a {planName} member!"
            values={{ planName: planContent.name.toLowerCase() }}
          />
        </Heading>
        <SubTitle data-testid="complete-subtitle">
          <FormattedMessage
            id="pixi_wizard.complete.subtitle"
            defaultMessage="Thank you for your subscription. Invoice and confirmation email has been sent to your email {email}"
            values={{ email: maskedEmail }}
          />
        </SubTitle>
      </Header>

      <SummaryCard data-testid="complete-summary">
        <SummaryRow>
          <RowLabel>
            <FormattedMessage
              id="pixi_wizard.complete.licenses"
              defaultMessage="{count, plural, one {{count} License} other {{count} Licenses}}"
              values={{ count: licenses }}
            />
          </RowLabel>
          <LicenseValue>
            <RowValue data-testid="complete-licenses-price">
              {price(subscriptionTotalCents)}
            </RowValue>
            <RenewSubtext data-testid="complete-renewal">
              {isYearly ? (
                <FormattedMessage
                  id="pixi_wizard.complete.renewed_yearly"
                  defaultMessage="Renewed every year"
                />
              ) : (
                <FormattedMessage
                  id="pixi_wizard.complete.renewed_monthly"
                  defaultMessage="Renewed every month"
                />
              )}
            </RenewSubtext>
          </LicenseValue>
        </SummaryRow>

        {isFreeDevice && (
          <>
            <Divider />
            <SummaryRow>
              <FreeDeviceLabel data-testid="complete-pixi-line">
                <FormattedMessage
                  id="pixi_wizard.complete.pixi_line"
                  defaultMessage="{count} free {pixiLogo}{count, plural, one {} other {s}} on your way!"
                  values={{
                    count: licenses,
                    pixiLogo: <PixiInlineLogo src={darkPixiIcon} alt="PIXI" />,
                  }}
                />
              </FreeDeviceLabel>
              <RowValue data-testid="complete-pixi-amount">
                {price(0)}{" "}
                <PixiSavings>(-{price(deviceSavingsCents)})</PixiSavings>
              </RowValue>
            </SummaryRow>
          </>
        )}
      </SummaryCard>

      <CompleteButtonRow>
        <Button primary onClick={onComplete} data-testid="complete-button">
          <FormattedMessage
            id="pixi_wizard.complete.complete"
            defaultMessage="Complete"
          />
        </Button>
      </CompleteButtonRow>
    </CompleteLayout>
  );
};
